import {
  assertReadFrameColumnNamesUsable,
  readFrameColumnOutputSocket,
  readFrameReservedOutputSockets,
} from "../../steps/data-access/stateful-table-read-frame.step";
import { LOOKUP_ROW_RESERVED_OUTPUT_SOCKET_IDS } from "../../steps/data-access/stateful-table-lookup-row.step";
import { FLOW_IN_SOCKET_ID } from "../../types";
import {
  buildDynamicSockets,
  effectiveSocketIdVersion,
  supportsSocketIdV2,
} from "./dynamicSocketId";
import type { DynamicSocketDefinition } from "./socketDefinition";
import { prettify } from "./socketDefinition";
import { statefulTableColumnTypeToSocketType } from "./statefulTableSocketTypes";

/** A column entry from a step's design-time schema snapshot. */
type ColumnSnapshot = { name: string; type: string };

/**
 * A column-derived socket set, plus the id each column ended up with.
 *
 * Handlers need the second half: they receive `(input, config, context)` and
 * never see their sockets, so they must resolve the same ids the canvas did to
 * read and write the payload with them.
 */
export interface ResolvedColumnSockets {
  sockets: DynamicSocketDefinition[];
  socketIdByColumnName: Map<string, string>;
}

/** Pair each column with the id `buildDynamicSockets` assigned it. */
function mapIds(
  columns: readonly ColumnSnapshot[],
  assignedIds: ReadonlyArray<string | null>,
): Map<string, string> {
  const byName = new Map<string, string>();
  columns.forEach((column, index) => {
    const id = assignedIds[index];
    if (id !== null) byName.set(column.name, id);
  });
  return byName;
}

/**
 * Normalize a step config's `columns` snapshot. It is persisted workflow config,
 * so a malformed entry degrades to being skipped rather than crashing socket
 * generation.
 */
function usableColumns(config: Record<string, unknown>): ColumnSnapshot[] {
  const columns = Array.isArray(config.columns) ? config.columns : [];
  return columns.filter(
    (c): c is ColumnSnapshot =>
      typeof c === "object" &&
      c !== null &&
      typeof (c as { name?: unknown }).name === "string" &&
      (c as { name: string }).name.length > 0 &&
      typeof (c as { type?: unknown }).type === "string",
  );
}

/**
 * Generate filter input sockets for stateful_table_read_frame. One optional
 * socket per column from the schema snapshot. Wiring a value filters the
 * frame to rows where that column equals the value; unwired sockets are
 * ignored. Multiple wired filters are AND-joined.
 */
export function resolveReadFrameFilterInputSockets(
  config: Record<string, unknown>,
): ResolvedColumnSockets {
  const columns = usableColumns(config);
  const { sockets, assignedIds } = buildDynamicSockets({
    // The engine strips `__flow_control_in__` from every step's input before
    // the handler sees it, so a column of that name would lose its filter
    // value with nothing to show for it.
    reserved: [FLOW_IN_SOCKET_ID],
    version: effectiveSocketIdVersion("stateful_table_read_frame", config),
    markedFallback: supportsSocketIdV2("stateful_table_read_frame"),
    entries: columns.map((c) => ({
      name: c.name,
      socket: {
        name: prettify(c.name),
        type: statefulTableColumnTypeToSocketType(c.type),
        required: false,
        description: `Optional exact-match filter on column "${c.name}"`,
      },
    })),
  });
  return { sockets, socketIdByColumnName: mapIds(columns, assignedIds) };
}

export function generateStatefulTableReadFrameInputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  return resolveReadFrameFilterInputSockets(config).sockets;
}

/**
 * Generate output sockets for stateful_table_read_frame: the six reserved
 * whole-frame sockets, plus ONE array socket per column from the schema
 * snapshot in config.
 *
 * The per-column arrays exist so the engine's lockstep fanout can zip them into
 * a downstream per-row step's scalar sockets (the `core_enum_fanout` pattern):
 * `rows` is positional (`array<json>`), which no scalar socket accepts and no
 * expression can index, so before these sockets there was no way to wire a
 * frame into a per-row step at all.
 *
 * Element types come from the same `statefulTableColumnTypeToSocketType` map the
 * filter inputs and `stateful_table_lookup_row` use, so one column is typed
 * identically everywhere — just wrapped in `array<...>` here.
 *
 * At version 1 a column name that would collide with a reserved socket id or
 * with another column throws (see `assertReadFrameColumnNamesUsable`) — the
 * editor turns that into a toast, because the snapshot comes from the selected
 * table and a table may legitimately have a column named `rows`. Silently
 * shadowing a socket would send the wrong data downstream.
 *
 * At version 2 the marker makes the reserved collision impossible, so only a
 * duplicate column name is still a problem — and a table cannot have two
 * columns of the same name, so in practice the check has nothing left to catch.
 */
export function resolveReadFrameColumnOutputSockets(
  config: Record<string, unknown>,
): ResolvedColumnSockets {
  const usable = usableColumns(config);
  const version = effectiveSocketIdVersion("stateful_table_read_frame", config);
  const markedFallback = supportsSocketIdV2("stateful_table_read_frame");

  // With no fallback available a colliding column has nowhere to go, so the
  // loud refusal is still the honest outcome; once the handler resolves its
  // keys through this call the collision is survivable and the assert would
  // reject a table the step can now handle.
  if (version < 2 && !markedFallback) {
    assertReadFrameColumnNamesUsable(usable.map((c) => c.name));
  }

  const reserved = readFrameReservedOutputSockets();
  const { sockets: columnSockets, assignedIds } = buildDynamicSockets({
    reserved: reserved.map((s) => s.id),
    version,
    markedFallback,
    entries: usable.map((c) => {
      const socket = readFrameColumnOutputSocket({
        columnName: c.name,
        cellSocketType: statefulTableColumnTypeToSocketType(c.type),
        displayName: prettify(c.name),
      });
      // `readFrameColumnOutputSocket` owns the socket's shape (its array
      // wrapping, its description); only the id is ours to derive.
      const { id: _derivedId, ...body } = socket;
      return { name: c.name, socket: body };
    }),
  });

  return {
    sockets: [...reserved, ...columnSockets],
    socketIdByColumnName: mapIds(usable, assignedIds),
  };
}

export function generateStatefulTableReadFrameOutputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  return resolveReadFrameColumnOutputSockets(config).sockets;
}

/**
 * Resolve stateful_table_lookup_row's per-column output sockets and the socket
 * id each configured column maps to, positionally.
 *
 * `statefulTableLookupRowHandler` keys its payload with those ids, and the
 * generator below builds its sockets from the same call — the handler never
 * sees its sockets, so deriving them separately is how the two drift.
 */
export function resolveLookupRowColumnSockets(
  config: Record<string, unknown>,
): ResolvedColumnSockets {
  const columns = usableColumns(config);
  const { sockets, assignedIds } = buildDynamicSockets({
    reserved: LOOKUP_ROW_RESERVED_OUTPUT_SOCKET_IDS,
    version: effectiveSocketIdVersion("stateful_table_lookup_row", config),
    // statefulTableLookupRowHandler resolves its payload keys through this same
    // call, so a legacy step whose table has a `frameNumber` column can have
    // the marked id rather than losing that column's socket.
    markedFallback: supportsSocketIdV2("stateful_table_lookup_row"),
    entries: columns.map((c) => ({
      name: c.name,
      socket: {
        name: prettify(c.name),
        type: statefulTableColumnTypeToSocketType(c.type),
        required: false,
        description: `Value of column "${c.name}" from the matched row`,
      },
    })),
  });

  return { sockets, socketIdByColumnName: mapIds(columns, assignedIds) };
}

/**
 * Generate output sockets for stateful_table_lookup_row. One socket per
 * column from the schema snapshot in config — the handler returns the
 * looked-up row's cell values keyed by the matching socket id.
 */
export function generateStatefulTableLookupRowOutputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  return resolveLookupRowColumnSockets(config).sockets;
}
